import { toast } from '@webapp/components/Notification'
import { type RequestTypeEnum } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { useIsMultiEntityEnabled } from '@webapp/core/hooks/useIsMultiEntityEnabled'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useQueryRequestableLegalEntities } from '../../models/useQueryRequestableLegalEntities'

export interface IUseRequestableLegalEntitiesPresenterOptions {
  requestType: RequestTypeEnum
  /**
   * Replaces the default load-error toast. A consumer that reports the
   * failure another way passes this, so a shared query is reported once.
   */
  onError?: () => void
}

/**
 * The Legal Entities the current user can raise `requestType` requests in,
 * with the one to preselect. The request only goes out while Multi-Entity
 * is enabled, since the backend 404s otherwise; with the gate off there are
 * no entities, no default and nothing to wait for.
 */
export const useRequestableLegalEntitiesPresenter = ({
  requestType,
  onError,
}: IUseRequestableLegalEntitiesPresenterOptions) => {
  const intl = useProcurifyIntl()
  const isMultiEntityEnabled = useIsMultiEntityEnabled()
  const { legalEntities, defaultLegalEntity, isLoading, isError } =
    useQueryRequestableLegalEntities({
      requestType,
      enabled: isMultiEntityEnabled,
      onError:
        onError ??
        (() => {
          toast.error({
            title: intl.formatMessage(messages.errorToastTitle),
            children: intl.formatMessage(messages.loadErrorToastMessage),
          })
        }),
    })

  return {
    data: { legalEntities, defaultLegalEntity },
    states: { isLoading, isError },
  }
}
